import "./reports.css"

import { Button, Col, notification, Row, Spin } from "antd";
import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import Logo from "../../assets/images/findem-full-logo-black.png";
import LogoWhite from "../../assets/images/findem-full-logo-white.png";
import ReportsIcon from "../../assets/images/reports-icon.png";
import OverviewReport from "./templates/overview";
import { useDispatch, useSelector } from "../../context/AppContext";
import { useUrlParams } from "../../utils/use-url-params";
import { actions } from "../../actions";
import { prettyCount } from "../../utils/format";
import useScript from "../../hooks/useScript";
import { DownloadOutlined, LinkOutlined, PrinterOutlined } from "@ant-design/icons";
import SearchInsights from "./templates/search-insights";
import { LEFT_INFO_CONTENT, MACROS_GROUP_BY_CAT, MATCHEST_BY_MAPPER, DEI_SETTING_INFO_KEYS } from "./common/constant";
import MatchesAPI from "../../apis/matches";
import { buildInsightsData, buildMatchesAnalyzePayloadByIcp, buildMatchesPayloadByIcp, buildMultiMatchesPayloadByIcp, setDynamicAnalyzeData, buildMultiMatchesV2PayloadByIcp, buildMatchesV2PayloadByIcp } from "./common/util";
import SearchAnalytics from "./templates/search-analytics";
import MatchesByAttribute from "./templates/matches-by-attribute";
import EnrichedProfileAPI from "../../apis/enriched-profile";
import SampleProfiles from "./templates/sample-profiles";
import Delayed from "./common/delayed";
import SearchAttributes from "./templates/search-attributes";
import EmailTemplatesAPI from "../../apis/email-templates";
import SearchCampaign from "./templates/search-campaign";
import IcpAPI from "../../apis/icp";
import ShortlistedProfiles from "./templates/shortlisted-profiles";
import axios from "axios";
import ProfileDetails from "./templates/profile-details";
import { appNextUrl, matchesUrl, searchUrl } from "findem-helpers";
import { identify } from "@fullstory/browser";
import useIsMultiSearch from "../../hooks/useIsMultiSearch";

const ReportWrapper = styled.div``;
const ReportTitleBar = styled(Row)``;
const ReportTitle = styled(Col)``;
const ReportTitlePool = styled(Col)`
  text-align: right;
  padding-right: 18px;
`;

const ReportContenBar = styled(Row)``;
const ReportContentBarInfo = styled(Col)``;
const ReportContentBarContent = styled(Col)``;

const InfoTitle = styled.div``;
const InfoSub = styled.div``;

const Title = styled.div``;

interface ReportSectionProps {
  id: string;
  sectionTitle: string;
  contentTitle?: React.ReactNode;
  leftInfoItems?: Array<{ key?: string; label: string; content: string }>;
  children: React.ReactNode;
  wrapperStyle?: React.CSSProperties;
  contentStyle?: React.CSSProperties;
  companyLogo: string;
  icpName: string;
  poolCount: number;
  slPubIds: string[];
}

const ReportSection: React.FC<ReportSectionProps> = ({
  id, sectionTitle, contentTitle, leftInfoItems,
  children, wrapperStyle, contentStyle,
  companyLogo, icpName, poolCount, slPubIds,
}) => {
  const showDEIDisclaimer = leftInfoItems?.some((info) => DEI_SETTING_INFO_KEYS.has(info.key));
  return (
    <ReportWrapper id={id} className="report-wrapper" style={wrapperStyle}>
      <ReportTitleBar className="report-title-bar">
        <ReportTitle span="18" className="report-title">
          {companyLogo
            ? <img className="custom-logo" src={companyLogo} alt="findem" width="60px" height="55px" />
            : <img className="logo" src={Logo} alt="findem" />}
          {icpName} | {sectionTitle}
        </ReportTitle>
        {poolCount > 0 && (
          <ReportTitlePool span="6">
            {`${prettyCount(poolCount)} Match Pool (${slPubIds?.length > 0 ? 'Shortlisted Candidates' : 'Incl. filtered matches'})`}
          </ReportTitlePool>
        )}
      </ReportTitleBar>
      <ReportContenBar className="report-content-bar">
        {leftInfoItems && leftInfoItems.length > 0 && (
          <ReportContentBarInfo className="report-content-bar-info" span="">
            <div>
              {leftInfoItems.map((info, idx) => (
                <React.Fragment key={`${id}-left-${idx}`}>
                  <InfoTitle className="info-title">{info.label}</InfoTitle>
                  <InfoSub className="info-sub">{info.content}</InfoSub>
                </React.Fragment>
              ))}
            </div>
            {showDEIDisclaimer && <InfoSub className="info-sub">Gender and ethnicity distribution is estimated through population-level statistical inference. It does not reflect the identification or classification of any individual.</InfoSub>}
          </ReportContentBarInfo>
        )}
        <ReportContentBarContent className="report-content-bar-container" style={contentStyle}>
          {contentTitle && <Title className="template-title">{contentTitle}</Title>}
          {children}
        </ReportContentBarContent>
      </ReportContenBar>
    </ReportWrapper>
  );
};

const ANALYTICS_REPORT = [
  { key: 'overall_exp', title: 'Matches by Overall Experience' },
  { key: 'curr_company_exp', title: 'Matches by Current Company Experience' },
  { key: 'curr_company_growth', title: 'Matches by Company Growth Rate' },
  { key: 'curr_company_emp_strength', title: 'Matches by Company Employee Size' },
  { key: 'curr_funding_stage', title: 'Matches by Company Funding Stage' }];


const PageLoader = styled.div`
  width: 100%;
  height: 100%;
  z-index: 2;
  display:block;
  position: fixed;
  background-color: #ffffff;
  opacity: 0.7;
`;

const TopBar = styled.div`
  position: fixed;
  display: block;
  width: 100%;
  height: 62px;
  background-color: #313131;
  z-index: 99999;
  @media print{
    display: none;
  }
  padding: 16px 0px;
`;

const LogoImage = styled.img`
  width: 140px;
`;

const TitlePage = styled.div`
  background-color: #12326E;
  width: 100%;
  height: 700px;
  display: inline-block;
  border-radius: 5px;
`

const PageButtonContainer = styled.span`
  text-align: center;
  color: #FFFFFF;
  position: fixed;
  top: 20px;
  right: 0;
  padding: 0px 23px;
  font-family: Roboto;
  font-size: 14px;
  font-weight: 500;
  float: right;
  display: inline-flex;
`;

const MATCHES_ANALYZE_COUNT = 10000;

const Reports: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { icpid, joid, isExecSearch, accountId, sandbox, count, childIcpId } = useUrlParams();
  const user = useSelector((state: any) => state.login.user);
  const [poolCount, setPoolCount] = useState<number>(0);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const currentIcp = useSelector((state: any) => state.icps.edit_icp);
  const [currentUpdatedIcp, setCurrentUpdatedIcp] = useState<any>({});
  const [icpProfiles, setIcpProfiles] = useState<any[]>([]);
  const [macrosAnalyticsData, setMacrosAnalyticsData] = useState<any>({});
  const [campaignsAnalyticsData, setCampaignsAnalyticsData] = useState<any>([]);
  const [atsAnalyticsData, setAtsAnalyticsData] = useState<any>({});
  const [rawAnalyzeMatchesData, setRawAnalyzeMatchesData] = useState<any>({});
  const [analyzeMatchesData, setAnalyzeMatchesData] = useState<any>({});
  const ats_config = useSelector((state: any) => state.ats.config);
  const [matches, setMatches] = useState<any>({});
  const [matchesPrfSummary, setMatchesPrfSummary] = useState<any>({});
  const skipDelay = false;
  const [openSearch, setOpenSearch] = useState<string>('');
  const [companyLogo, setCompanyLogo] = useState<string>('');
  const [spinMessage, setSpinMessage] = useState<string>('We are generating your search report...');
  const [showDownload, setShowDownload] = useState<boolean>(false);
  const [slPubIds, setSlPubIds] = useState<string[]>([]);
  const [deiDisabled, setDeiDisabled] = useState<boolean>(false);
  const allMacros = useSelector((state: any) => state.attrs_state.all_macros);
  const org_settings = useSelector(
    (state: any) => state.analytics.org_settings
  );

  // cache
  // @ts-ignore
  window._reports_cache = window._reports_cache || {};

  useScript('https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js');
  useScript('https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.4.0/jspdf.umd.min.js');

  const isMultiSearch = useIsMultiSearch({ icp: currentUpdatedIcp });

  const activeChildSearch = useMemo(() => {
    if (!isMultiSearch || !currentUpdatedIcp) {
      return null;
    }
    const childIcpFromUrlParam = currentUpdatedIcp.child_icps.find((childIcp: any) => childIcp?.icp?.ref === childIcpId)?.icp;
    const fallbackChildIcp = currentUpdatedIcp.child_icps[0]?.icp;

    return childIcpFromUrlParam ?? fallbackChildIcp ?? null;
  }, [isMultiSearch, currentUpdatedIcp, childIcpId]);

  // prefer child icp if multi search else use current icp
  const effectiveSearch = useMemo(() => isMultiSearch ? (activeChildSearch ?? currentUpdatedIcp) : currentUpdatedIcp, [isMultiSearch, activeChildSearch, currentUpdatedIcp]);

  // Fetch user info on mount
  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const response = await fetch(`${matchesUrl}/api/user_info`, {
          method: "GET",
          credentials: "include",
          headers: {
            "accept": "*/*",
            "content-type": "application/json",
          }
        });
        if (response.ok) {
          const userData = await response.json();
          dispatch({ type: 'SET_USER', payload: userData });
        }
      } catch (error) {
        console.error("Failed to fetch user info:", error);
      }
    };

    if (!user.uid) {
      fetchUserInfo();
    }
  }, []);

  useEffect(() => {
    if (user.uid) {
      dispatch(actions.getMacrosRequest({
        id: joid || user.uid,
        callConnection: false
      }));
    }
  }, [joid, user]);

  useEffect(() => {
    if (user?.uid) {
      identify(user.uid, {
        name: user.name,
        email: user.email,
        ...(user.company ? { company: user.company } : {})
      });
    }
  }, [user?.uid, user?.name, user?.email, user?.company]);

  useEffect(() => {
    setIsProcessing(true);
    if (!icpid) {
      navigate('/unauthenticated');
      return;
    }
    document.body.className += ' reports-page';
  }, [icpid, navigate]);

  // load org pref...
  useEffect(() => {
    if (user.uid) {
      const _joid = joid || user.uid;
      dispatch(actions.getOrgSettingsRequest({ joid: _joid}));
    }
  }, [ user]);

  // load icp
  useEffect(() => {
    if (icpid && user.uid && org_settings && Object.keys(org_settings).length > 0) {
      const payload: any = { icpid };
      payload['joid'] = joid || user.uid;
      dispatch(
        actions.getIcpByIdRequest(payload)
      );
    }
  }, [icpid, user, org_settings]);

  // load ats info for checking account has ats..
  useEffect(() => {
    if (user.uid) {
      dispatch(actions.getAtsConfigRequest({ joid }));
    }
  }, [user]);

  const stripHtml = (html: string): string => {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.body.textContent || '';
  }

  useEffect(() => {
    const getIcpProfiles = async () => {
      const _match_feedback = currentIcp?.cxt_data?.match_feedback || {};
      const _prids = _match_feedback?.filter(match => match.shortlisted && !match?.archived).map((match: any) => match.prid);
      if(_prids.length === 0){
        return;
      }
      const _profiles = await IcpAPI.fetchProfilesByPrId({
        joid: joid || user.uid,
        prids: _prids
      });
      const _prfs_notes = await IcpAPI.fetchProfileNotesByPrIds({
        joid: joid || user.uid,
        prids: _prids.slice(0, 100)  // first 100
      });
      const prfsByCandidateRefs = await IcpAPI.fetchProfilesByCandidateRef({
        joid: joid || user.uid,
        prids: _prids
      });
      const _pubPrids = [];
      if(prfsByCandidateRefs && prfsByCandidateRefs.data){
        const prfsByCandidateRef = prfsByCandidateRefs.data;
        if(prfsByCandidateRef?.results){
          const prfsByCandidateRefResults = prfsByCandidateRef.results;
          Object.values(prfsByCandidateRefResults).forEach((prf: any) => {
            if(!prf.sandbox){
              _pubPrids.push(prf.ref);
            }
          });
        }
      }
      Object.keys(_profiles.data).forEach((prid: string) => {
        const _icp_notes = _prfs_notes?.data[prid]?.filter((note: any) => note.icp_id === currentIcp?.ref).map((note: any) => stripHtml(note.notes_txt)) || [];
        _profiles.data[prid].notes = _icp_notes || [];
      });
      setSlPubIds(_pubPrids);
      setIcpProfiles(_profiles.data ? Object.values(_profiles.data) : []);
    }
    if (user.uid && isExecSearch && currentIcp?.ref) {
      getIcpProfiles();
    }
  }, [joid, user, currentIcp, isExecSearch]);

  useEffect(() => {
    if (currentIcp?.ref && (!isExecSearch || (isExecSearch && slPubIds.length > 0))) {
      setCurrentUpdatedIcp(currentIcp);
    }
  },[currentIcp, isExecSearch, slPubIds]);

// fetc analyze data...
  useEffect(() => {
    const getMatchesInsights = async () => {
      const payload: any = await buildMatchesAnalyzePayloadByIcp(effectiveSearch, joid || user.uid, count ? parseInt(count) : MATCHES_ANALYZE_COUNT , slPubIds, org_settings.domain_to_exclude_search);
      if(sandbox){
        payload['sandbox'] = true;
      }
      try {
        const response = currentUpdatedIcp?.mode === 'v2' ? await MatchesAPI.makeMatchesAnalyseV2APICall(payload) : await MatchesAPI.makeMatchesAnalyseAPICall(payload);
        const _processed_response = setDynamicAnalyzeData(response.data);
        setRawAnalyzeMatchesData(_processed_response);
        setAnalyzeMatchesData(buildInsightsData(_processed_response));
      } catch (error) {
        console.log("===error",error);
      }
    }
    if (effectiveSearch?.cxt_data && (!isExecSearch || (isExecSearch && slPubIds.length > 0))) {
      getMatchesInsights();
    }
    if (currentUpdatedIcp) {
      const openSearch = currentUpdatedIcp?.mode === 'v2' ?
        `${appNextUrl}/searches/${currentUpdatedIcp.ref}?joid=${joid || user.uid}` :
        `${searchUrl}/pub/searchv2?matches_view=true&uid=${user.uid}&joid=${joid || user.uid}&open_icpid=${currentUpdatedIcp.ref}&view=sllist`
      setOpenSearch(openSearch);
    }
  }, [currentUpdatedIcp, org_settings, slPubIds, isExecSearch, joid, user, slPubIds, effectiveSearch]);

  // fetch multimatch data for stats.
  useEffect(() => {
    const getMatchesAnalytics = async () => {
      const payload: any = effectiveSearch?.mode === 'v2' ? await buildMultiMatchesV2PayloadByIcp(effectiveSearch, joid || user.uid, org_settings.domain_to_exclude_search)  : await buildMultiMatchesPayloadByIcp(effectiveSearch, joid || user.uid);
      try {
        payload['joid'] = joid || user.uid;
        const _to_matches: string[] = [...Object.keys(MACROS_GROUP_BY_CAT['overall_exp']), ...Object.keys(MACROS_GROUP_BY_CAT['curr_company_exp']),
        ...Object.keys(MACROS_GROUP_BY_CAT['curr_company_growth']), ...Object.keys(MACROS_GROUP_BY_CAT['curr_company_emp_strength']),
          ...Object.keys(MACROS_GROUP_BY_CAT['curr_funding_stage'])];
        payload['to_match'] = _to_matches;
        if(sandbox){
          payload['sandbox'] = true;
        }
        if(slPubIds){
          payload['prids'] = slPubIds;
        }
        const response = effectiveSearch?.mode === 'v2' ? await MatchesAPI.makeMultiMatchesV2APICall(payload) : await MatchesAPI.makeMultiMatchesAPICall(payload);
        if(accountId) {
          const logos = await axios(`${matchesUrl}/hm/api/tc/master-signup-form-logos?account_id=${accountId}`, {
            method: "GET",
            withCredentials: true,
          })
          setCompanyLogo(logos.data[Object.keys(logos.data)[0]])
          setMacrosAnalyticsData(response.data);
          setTimeout(() => {
            setIsProcessing(false);
          }, 1000);
        } else {
          setMacrosAnalyticsData(response.data);
          setTimeout(() => {
            setIsProcessing(false);
          }, 1000);
        }
      } catch (error) {
        console.log('error', error);
      }
    }
    if (effectiveSearch?.cxt_data && (!isExecSearch || (isExecSearch && slPubIds.length > 0))) {
      setTimeout(() => {
        getMatchesAnalytics();
      }, 1000);
    }
  }, [effectiveSearch, isExecSearch, joid, org_settings, slPubIds, user.uid]);

  useEffect(() => {
    const getAtsAnalytics = async () => {
      const payload: any = effectiveSearch?.mode === 'v2' ? await buildMultiMatchesV2PayloadByIcp(effectiveSearch, joid || user.uid, org_settings.domain_to_exclude_search) : await buildMultiMatchesPayloadByIcp(effectiveSearch, joid || user.uid);
      if(sandbox){
        payload['sandbox'] = true;
      }
      try {
        if(slPubIds){
          payload['prids'] = slPubIds;
        }
        payload['joid'] = joid || user.uid;
        const _to_matches: string[] = [...Object.keys(MACROS_GROUP_BY_CAT['ats_source'])];
        payload['to_match'] = _to_matches;
        payload['sandbox'] = true;
        const response = effectiveSearch?.mode === 'v2' ? await MatchesAPI.makeMultiMatchesV2APICall(payload)  : await MatchesAPI.makeMultiMatchesAPICall(payload);
        setAtsAnalyticsData(response.data);
      } catch (error) {
        console.log('error', error);
      }
    }
    if (effectiveSearch?.cxt_data && ats_config?.length && (!isExecSearch || (isExecSearch && slPubIds.length > 0))) {
      setTimeout(() => {
        getAtsAnalytics();
      }, 1000);
    }
  }, [effectiveSearch, ats_config, org_settings, isExecSearch, slPubIds, joid, user.uid]);

  // fetch multimatch data for stats.
  useEffect(() => {
    const getMatches = async () => {
      const payload: any = effectiveSearch?.mode === 'v2' ? buildMatchesV2PayloadByIcp(effectiveSearch) : buildMatchesPayloadByIcp(effectiveSearch);
      if(sandbox){
        payload['sandbox'] = true;
      }
      if(slPubIds){
        payload['prids'] = slPubIds;
      }
      try {
        payload['joid'] = joid || user.uid;
        const response = effectiveSearch?.mode === 'v2' ? await MatchesAPI.makeMatchesLiteV2APICall(payload)  : await MatchesAPI.makeMatchesLiteAPICall(payload);
        const { profiles } = response.data;
        // filtering prfs without data
        const _filteredPrf: string[] = [];
        const _prfs = Object.keys(profiles);
        for (const prf of _prfs) {
          if (profiles[prf]?.candidate && profiles[prf]?.ext_refs?.length > 0 && profiles[prf]?.workexp?.length > 0) {
            _filteredPrf.push(prf);
          }
          if (_filteredPrf.length === 6) {
            break;
          }
        }
        if (_filteredPrf.length > 0) {
          try {
            const _profilesSummary = await EnrichedProfileAPI.fetchProfilesSummaryByPost({ prids: _filteredPrf, joid: joid || user.uid});
            setMatchesPrfSummary(_profilesSummary.data);
          // eslint-disable-next-line no-empty
          } catch (err) {
          }
        }
        setMatches(response.data);
      // eslint-disable-next-line no-empty
      } catch (err) {
      }
    }
    if (effectiveSearch?.cxt_data && !isExecSearch) {
      setTimeout(() => {
        getMatches();
      }, 2000);
    }
  }, [effectiveSearch, isExecSearch, joid, slPubIds, user.uid]);

  // icp campaign stats..
  useEffect(() => {
    const getCampaignsStats = async () => {
      const payload: any = {
        summary: true,
        icp_ids: [currentUpdatedIcp.ref],
        joid: joid || user.uid
      }
      const resp: any = await EmailTemplatesAPI.getIcpCampaignStats(payload);
      setCampaignsAnalyticsData(resp.data?.icp_stats[0] || []);
    }
    if (currentUpdatedIcp?.cxt_data && (!isExecSearch || (isExecSearch && slPubIds.length > 0))) {
      setTimeout(() => {
        getCampaignsStats();
      }, 3000);
    }
  }, [currentUpdatedIcp, isExecSearch, joid, slPubIds, user.uid]);
  // show download button after all data loaded.
  useEffect(() => {
    if (analyzeMatchesData.top_curr_comps &&
      macrosAnalyticsData.base_filter_count &&
      matches &&
      !isExecSearch &&
      !isProcessing) {
      setTimeout(() => {
        setShowDownload(true);
      }, 5000);
    } else if(!isProcessing && isExecSearch){
        setTimeout(() => {
          setShowDownload(true);
        }, 10000);
    }
  }, [
    analyzeMatchesData.top_curr_comps,
    macrosAnalyticsData.base_filter_count,
    campaignsAnalyticsData,
    matches,
    isProcessing
  ]);
  // logic to handle pdf loads.
  const downloadPDF = async () => {
    setIsProcessing(true);

    const eles = document.getElementsByTagName('a');
    for (const ele of eles) {
      ele.style.display = 'none';
    }

    const opt: any = {
      filename: `${unescape(currentUpdatedIcp.name.replaceAll('/', '-'))} - Generated on ${new Date().toLocaleDateString('en-US').replaceAll('/', '-')} at ${new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }).replaceAll(':', '.')}.pdf`,
      html2canvas: { scrollY: 0, scale: 2, letterRendering: true, useCORS: true },
      jsPDF: { unit: 'mm', format: [305, 200], orientation: 'landscape' },
      margin: [8, 0],
    };
    // @ts-ignore
    const doc = new window.jspdf.jsPDF(opt.jsPDF);
    // @ts-ignore
    const domElementCollection = document.getElementsByClassName('page-wrapper');
    const pages = [...domElementCollection].map((htmlElement) => {
      return {
        html: htmlElement.outerHTML,
        element: htmlElement,
        isProfilesListPage: htmlElement.classList.contains('sl-profiles-wrapper'),
        isProfileDetailPage: htmlElement.classList.contains('sl-profile-wrapper')
      };
    });
    for (let i = 0; i < pages.length; i++) {
      const page = pages[i];

      if (page.isProfilesListPage) {
        // For profiles list page, use landscape format same as other pages
        const listOpt = {
          ...opt,
          html2canvas: {
            scrollY: 0,
            scrollX: 0,
            scale: 2,
            letterRendering: true,
            useCORS: true,
            allowTaint: true
          },
          jsPDF: { unit: 'mm', format: [305, 200], orientation: 'landscape' },
          margin: [8, 0],
        };
        // @ts-ignore
        const pageImage = await window.html2pdf().from(page.html).set(listOpt).outputImg();

        if (i != 0) {
          doc.addPage([305, 200], 'landscape');
        }
        doc.addImage(pageImage.src, 'jpeg', 8, 0, 270, 200);
      } else if (page.isProfileDetailPage) {
        // For individual profile detail pages, use natural dimensions (this was working!)
        const detailOpt = {
          ...opt,
          html2canvas: {
            scrollY: 0,
            scrollX: 0,
            scale: 1,
            letterRendering: true,
            useCORS: true,
            allowTaint: true
          },
          margin: [0, 0],
        };
        // @ts-ignore
        const pageImage = await window.html2pdf().from(page.html).set(detailOpt).outputImg();

        // Create image to get actual dimensions
        const tempImg = new Image();
        tempImg.src = pageImage.src;
        await new Promise((resolve) => { tempImg.onload = resolve; });

        // Convert pixel dimensions to mm (96 DPI)
        const imgWidthMm = (tempImg.width * 25.4) / 96;
        const imgHeightMm = (tempImg.height * 25.4) / 96;

        if (i != 0) {
          doc.addPage([imgWidthMm, imgHeightMm]);
        }
        doc.addImage(pageImage.src, 'jpeg', 0, 0, imgWidthMm, imgHeightMm);
      } else {
        // For other pages, use landscape format
        // @ts-ignore
        const pageImage = await window.html2pdf().from(page.html).set(opt).outputImg();
        if (i != 0) {
          doc.addPage([305, 200], 'landscape');
        }
        doc.addImage(pageImage.src, 'jpeg', opt.margin[0], opt.margin[1], 270, 200);
      }

      setSpinMessage(`We are generating your PDF report...(${i + 1} of ${pages.length} pages. Done)`);
      if (i+1 == pages.length) {
        for (const ele of eles) {
          ele.style.display = 'inline-block';
        }
      }
    }
    setIsProcessing(false);
    doc.save(opt.filename);
  }

  const printPage = () => {
    window.print();
  }

  const copyToClipboard = () => {
    window.navigator.clipboard.writeText(window.location.href);
    notification.open({
      message: 'Link is copied.',
      placement: 'bottomRight',
      duration: 2
    });
  }

  const leftInfoContent = useMemo(() => {
    if(deiDisabled){
      return Object.keys(LEFT_INFO_CONTENT).reduce((acc: any, key: string) => {
        const optionsList = LEFT_INFO_CONTENT[key];
        const optionsListFiltered = optionsList.filter((info) => !DEI_SETTING_INFO_KEYS.has(info.key));
        acc[key] = optionsListFiltered;
        return acc;
      }, {} as {[key: string]: {
        key: string,
        label: string,
        content: string
      }[]});
    }else{
      return LEFT_INFO_CONTENT;
    }
  }, [deiDisabled]);

  return (<>
    <PageLoader style={{ display: `${isProcessing ? '': 'none'}`}}>
      <Spin size="large" tip={spinMessage} style={{ top: '50%', position: 'relative', color:'#313131', fontSize: '14px', fontWeight: 500 }}>
      </Spin>
    </PageLoader>
    <TopBar>
      <LogoImage src={LogoWhite}></LogoImage>
      <PageButtonContainer>
        {showDownload && (<>
          <Button icon={<DownloadOutlined />} type="primary" size={'small'} style={{ borderRadius: '5px', margin: '0px 3px', fontWeight: 500 }} onClick={() => { downloadPDF() }}>Download Search Report</Button>
          <Button icon={<LinkOutlined />} size={'small'} style={{ borderRadius: '5px', margin: '0px 3px', backgroundColor: '#C5E0FF', color: '#2A5081', fontWeight: 500 }} onClick={() => { copyToClipboard() }}> Copy Link</Button>
          <Button icon={<PrinterOutlined />} size={'small'} style={{ borderRadius: '5px',margin: '0px 3px', backgroundColor: '#C5E0FF', color: '#2A5081', fontWeight: 500 }} onClick={() => { printPage() }}/>
        </>)}
      </PageButtonContainer>
    </TopBar>
    {/* <PageWrapper id="download-as-pdf" className="page-wrapper"> */}
      {!isProcessing && <div>
        {currentUpdatedIcp.ref && (<>
          <div className="page-wrapper">
            <ReportWrapper id="search-title" className="report-wrapper" style={{marginTop: '70px'}}>
              <TitlePage>
                <div>
                  {companyLogo ? <img src={companyLogo} style={{ width: '96px', height: '21px', margin: '20px' }} /> : <img src={LogoWhite} style={{ width: '110px', margin: '20px' }} />}
                </div>
                <div style={{ textAlign: 'center', color: '#FFFFFF', position: 'relative', top: '130px', padding: '0px 100px' }}>
                  <span style={{ display: 'block' }}><img src={ReportsIcon} style={{ width: '73px', marginBottom: '20px' }}></img></span>
                  <span style={{ fontWeight: 600, fontSize: '50px', display: 'block' }}>{unescape(currentUpdatedIcp.name)}</span>
                  <span style={{ fontWeight: 500, fontSize: '20px', display: 'block', marginTop: '40px' }}>Created on {new Date().toLocaleDateString()}</span>
                  <span style={{ fontWeight: 500, fontSize: '20px', display: 'block', marginTop: '5px' }}>By {user.name}</span>
                </div>
              </TitlePage>
            </ReportWrapper>
          </div>

          <div className="html2pdf__page-break"></div>
          {allMacros && Object.keys(allMacros).length > 0 && <div className="page-wrapper">
            <ReportSection
              id="overview"
              sectionTitle="Search Attributes"
              contentTitle={<>{unescape(currentUpdatedIcp.name)} <a className="anchor-links" style={{fontSize: '10px', fontWeight: 400}} href={`${openSearch}`} target="_blank" rel="noreferrer">Open Search</a></>}
              leftInfoItems={leftInfoContent.attributes}
              companyLogo={companyLogo} icpName={unescape(currentUpdatedIcp.name)} poolCount={poolCount} slPubIds={slPubIds}
            >
              <SearchAttributes effectiveSearch={effectiveSearch} />
            </ReportSection>
          </div>}

          <div className="html2pdf__page-break"></div>
          {/* overview reports */}
          <div className="page-wrapper">
            <ReportSection
              id="overview"
              sectionTitle="Overview"
              contentTitle="Key Insights of Your Search"
              leftInfoItems={leftInfoContent.overview}
              companyLogo={companyLogo} icpName={unescape(currentUpdatedIcp.name)} poolCount={poolCount} slPubIds={slPubIds}
            >
              <OverviewReport
                analyzeMatchesData={analyzeMatchesData}
                currentIcp={currentUpdatedIcp}
                slPubIds={slPubIds}
                key="overview-report"
                setPoolCount={setPoolCount}
                effectiveSearch={effectiveSearch}
                disableDEI={deiDisabled}
                setDisableDEI={setDeiDisabled}
              />
            </ReportSection>
          </div>

          <div className="html2pdf__page-break"></div>
          {/* Above layout will be repeated.... */}

          {analyzeMatchesData.top_curr_comps && (
            <Delayed waitBeforeShow={100} skipDelay={skipDelay}>
              <div className="page-wrapper">
                <ReportSection
                  id="overview_insights"
                  sectionTitle="Overview"
                  contentTitle={<>Key Insights of Your Search<span style={{ color: '#808080', fontSize: '12px' }}>{poolCount > 10000 ? ' (Data sampled from top 10,000 profiles)' : ''}</span></>}
                  leftInfoItems={leftInfoContent.insights}
                  companyLogo={companyLogo} icpName={unescape(currentUpdatedIcp.name)} poolCount={poolCount} slPubIds={slPubIds}
                >
                  <SearchInsights analyzeMatchesData={analyzeMatchesData} key="overview-report" />
                </ReportSection>
              </div>

              <div className="html2pdf__page-break"></div>
            </Delayed>
          )}

          {/* Search analytics */}
          {macrosAnalyticsData.base_filter_count && ANALYTICS_REPORT.map((reportFor: any, idx: number) => (<>
            <Delayed waitBeforeShow={200 + 60 * (idx + 1)} skipDelay={skipDelay}>
              <div className="page-wrapper">
                <ReportSection
                  id={reportFor.key}
                  sectionTitle={reportFor.title}
                  contentTitle={reportFor.title}
                  leftInfoItems={leftInfoContent[reportFor.key]}
                  companyLogo={companyLogo} icpName={unescape(currentUpdatedIcp.name)} poolCount={poolCount} slPubIds={slPubIds}
                >
                  <SearchAnalytics type={reportFor.key} data={macrosAnalyticsData} title={reportFor.title} deiDisabled={deiDisabled} />
                </ReportSection>
              </div>

              <div className="html2pdf__page-break"></div>
            </Delayed>
          </>))}

          {atsAnalyticsData.base_filter_count && ats_config?.length && (<>
            <Delayed waitBeforeShow={300} skipDelay={skipDelay}>
              <div className="page-wrapper">
                <ReportSection
                  id="matches_by_ats"
                  sectionTitle="Matches by Applicant Tracking System (ATS) Source"
                  contentTitle="Matches by Applicant Tracking System (ATS) Source"
                  leftInfoItems={leftInfoContent['ats_source']}
                  companyLogo={companyLogo} icpName={unescape(currentUpdatedIcp.name)} poolCount={poolCount} slPubIds={slPubIds}
                >
                  <SearchAnalytics type={'ats_source'} data={atsAnalyticsData} title={'Matches by Applicant Tracking System (ATS) Source'} deiDisabled={deiDisabled} />
                </ReportSection>
              </div>

              <div className="html2pdf__page-break"></div>
            </Delayed>
          </>)}

          {/* Matches By attributes */}
          {analyzeMatchesData.top_locations && rawAnalyzeMatchesData.analysis_info && Object.keys(MATCHEST_BY_MAPPER).map((reportFor: any, idx: number) => (<>
            <Delayed waitBeforeShow={500 + 500 * (idx + 1)} skipDelay={skipDelay}>
              <div className="page-wrapper">
                <ReportSection
                  id={reportFor}
                  sectionTitle={MATCHEST_BY_MAPPER[reportFor]["title"]}
                  contentTitle={<>{MATCHEST_BY_MAPPER[reportFor]["title"]}<span style={{ color: '#c2c2c2', fontSize: '12px' }}>{poolCount > 10000 ? ' (Data sampled from top 10,000 profiles)' : ''}</span></>}
                  leftInfoItems={leftInfoContent[reportFor]}
                  contentStyle={Boolean(leftInfoContent[reportFor]?.length) ? undefined : {width: '1141px', marginLeft: '20px', height: 'unset'}}
                  companyLogo={companyLogo} icpName={unescape(currentUpdatedIcp.name)} poolCount={poolCount} slPubIds={slPubIds}
                >
                  <MatchesByAttribute data={{
                    formated_data: analyzeMatchesData,
                    raw_data: rawAnalyzeMatchesData.analysis_info,
                    type: MATCHEST_BY_MAPPER[reportFor]['type'],
                    identifier: reportFor
                  }} deiDisabled={deiDisabled} key="overview-report" />
                </ReportSection>
              </div>
              <div className="html2pdf__page-break"></div>
            </Delayed>
          </>))}
        {/* Campaigns data */}
        {campaignsAnalyticsData?.campaign_summary_stats?.length > 0 && campaignsAnalyticsData?.campaign_summary_stats.map((campaign: any, idx: number) => (<>
          {Object.keys(campaign.all_emails_phase_stats_map).length && (<>
            <Delayed waitBeforeShow={500 + 500 * (idx + 1)} skipDelay={skipDelay}>
              <div className="page-wrapper">
                <ReportSection
                  id={campaign.icpid}
                  sectionTitle="Response Rate"
                  contentTitle={<>Campaign Report - {campaign.campaign_name} <a className="anchor-links" style={{ fontSize: '10px', fontWeight: 400 }} href={`${matchesUrl}/hm/shortlists_slonly?&joid=${joid || user.uid}&icpid=${currentUpdatedIcp.ref}&ppool=true&frs=true&slonly=true&prod=true`} target="_blank" rel="noreferrer">Campaign Url</a></>}
                  leftInfoItems={leftInfoContent['campaign_report']}
                  companyLogo={companyLogo} icpName={unescape(currentUpdatedIcp.name)} poolCount={poolCount} slPubIds={slPubIds}
                >
                  <SearchCampaign campStats={campaign} />
                </ReportSection>
              </div>
              <div className="html2pdf__page-break"></div>
            </Delayed>
          </>)}
        </>))}

          {!isExecSearch && Object.keys(matches).length > 0 && Object.keys(matchesPrfSummary).length > 0 && (
            <Delayed waitBeforeShow={2000} skipDelay={skipDelay}>
              <div className="page-wrapper">
                <ReportSection
                  id="sample_profiles"
                  sectionTitle="Sample Profiles"
                  contentTitle="Sample Profiles"
                  contentStyle={{width: '1120px', marginLeft: '20px'}}
                  companyLogo={companyLogo} icpName={unescape(currentUpdatedIcp.name)} poolCount={poolCount} slPubIds={slPubIds}
                >
                  <SampleProfiles matches={matches} matchesPrfSummary={matchesPrfSummary} />
                </ReportSection>
              </div>
              <div className="html2pdf__page-break"></div>
            </Delayed>
          )}

          {isExecSearch && icpProfiles && (() => {
            // Split profiles into pages (5 profiles per page to avoid stretching)
            const profilesPerPage = 5;
            const profileKeys = Object.keys(icpProfiles);
            const totalPages = Math.ceil(profileKeys.length / profilesPerPage);
            const pages = [];

            for (let pageNum = 0; pageNum < totalPages; pageNum++) {
              const startIdx = pageNum * profilesPerPage;
              const endIdx = Math.min(startIdx + profilesPerPage, profileKeys.length);
              const pageProfiles = {};

              for (let i = startIdx; i < endIdx; i++) {
                const key = profileKeys[i];
                pageProfiles[key] = icpProfiles[key];
              }

              pages.push(
                <Delayed key={`profiles-page-${pageNum}`} waitBeforeShow={2000} skipDelay={skipDelay}>
                  <div className="page-wrapper sl-profiles-wrapper">
                    <ReportSection
                      id={`sample_profiles_${pageNum}`}
                      sectionTitle={`Current Profiles${totalPages > 1 ? ` (Page ${pageNum + 1} of ${totalPages})` : ''}`}
                      contentTitle="Current Profiles"
                      wrapperStyle={{height: 'auto'}}
                      contentStyle={{width: '1141px', marginLeft: '20px', height: 'auto'}}
                      companyLogo={companyLogo} icpName={unescape(currentUpdatedIcp.name)} poolCount={poolCount} slPubIds={slPubIds}
                    >
                      <ShortlistedProfiles data={pageProfiles}/>
                    </ReportSection>
                  </div>
                  <div className="html2pdf__page-break"></div>
                </Delayed>
              );
            }

            return pages;
          })()}
          {isExecSearch && icpProfiles && icpProfiles.map((icpProfile: any, idx: number) => (<>
            <Delayed waitBeforeShow={2000} skipDelay={skipDelay}>
              <div className="page-wrapper sl-profile-wrapper">
                <ReportSection
                  id={`sample_profile-${idx}`}
                  sectionTitle={decodeURI(icpProfile.candidate.full_name)}
                  wrapperStyle={{height: 'unset'}}
                  contentStyle={{width: '1141px', marginLeft: '20px', height: 'unset'}}
                  companyLogo={companyLogo} icpName={unescape(currentUpdatedIcp.name)} poolCount={poolCount} slPubIds={slPubIds}
                >
                  <ProfileDetails data={icpProfile}/>
                </ReportSection>
              </div>
              <div className="html2pdf__page-break"></div>
            </Delayed>
          </>))}
        </>)}
      </div>}
    {/* </PageWrapper> */}
  </>);
};

export default Reports;
